// components/groupAdmin/feed/AdminFeedTab.jsx
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { adminFeedApi } from './feedApi';

import ArticleCreateCard from './create/ArticleCreateCard';
import CourseCreateCard from './create/CourseCreateCard';
import FeedList from './list/FeedList';
import EditFeedItemDialog from './dialogs/EditFeedItemDialog';
import CourseBuilderDialog from './course/CourseBuilderDialog';

/**
 * Admin Feed Tab:
 * - Create either Article post (existing behavior) or Course (new)
 * - List mixed feed items
 * - Edit common fields + poll
 * - Manage course modules/lessons via CourseBuilderDialog
 */
const AdminFeedTab = ({ groupId }) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [feedItems, setFeedItems] = useState([]);

  const [createType, setCreateType] = useState('article'); // 'article' | 'course'

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Course builder
  const [builderOpen, setBuilderOpen] = useState(false);
  const [activeCourseId, setActiveCourseId] = useState(null);

  const loadFeed = async () => {
    setLoading(true);
    try {
      const data = await adminFeedApi.listFeed(groupId);
      setFeedItems(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || t('groupAdminTabs.feed.toastLoadError', { defaultValue: 'Failed to load feed' }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFeed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  const handleRemove = async (itemId) => {
    if (!window.confirm(t('groupAdminTabs.feed.toastDeleteConfirm', { defaultValue: 'Remove this feed item?' }))) return;

    try {
      await adminFeedApi.deleteFeedItem(groupId, itemId);
      setFeedItems((prev) => prev.filter((f) => f.id !== itemId));
      toast.success(t('groupAdminTabs.feed.toastDeleteSuccess', { defaultValue: 'Removed' }));
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || t('groupAdminTabs.feed.toastDeleteError', { defaultValue: 'Failed to remove' }));
    }
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setEditOpen(true);
  };

  const openCourseBuilderFromFeedItem = (item) => {
    // supports multiple shapes
    const courseId =
      item?.content_ref?.collection === 'courses'
        ? item.content_ref.id
        : item?.course_id || item?.courseId || item?.course?.id || null;

    if (!courseId) {
      toast.error('Course id not found on this feed item.');
      return;
    }

    setActiveCourseId(courseId);
    setBuilderOpen(true);
  };

  const handleCourseCreated = ({ courseId }) => {
    // Refresh feed list so it shows new course post
    loadFeed();
    if (courseId) {
      setActiveCourseId(courseId);
      setBuilderOpen(true);
    }
  };

  return (
    <div className="grid lg:grid-cols-[2fr,3fr] gap-6">
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant={createType === 'article' ? 'default' : 'outline'}
            onClick={() => setCreateType('article')}
          >
            {t('groupAdminTabs.feed.createPostTab', { defaultValue: 'Post' })}
          </Button>

          <Button
            type="button"
            variant={createType === 'course' ? 'default' : 'outline'}
            onClick={() => setCreateType('course')}
          >
            {t('groupAdminTabs.feed.createCourseTab', { defaultValue: 'Course' })}
          </Button>
        </div>

        {createType === 'article' ? (
          <ArticleCreateCard groupId={groupId} onCreated={loadFeed} />
        ) : (
          <CourseCreateCard groupId={groupId} onCourseCreated={handleCourseCreated} />
        )}
      </div>

      <FeedList
        loading={loading}
        items={feedItems}
        onRemove={handleRemove}
        onEdit={openEdit}
        onEditCourse={openCourseBuilderFromFeedItem}
      />

      <EditFeedItemDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        groupId={groupId}
        item={editingItem}
        onUpdated={loadFeed}
      />

      <CourseBuilderDialog
        open={builderOpen}
        onOpenChange={setBuilderOpen}
        groupId={groupId}
        courseId={activeCourseId}
        onSaved={loadFeed}
      />
    </div>
  );
};

export default AdminFeedTab;
